"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.foldSymbolicValue = foldSymbolicValue;
const signal_name_normalization_1 = require("../DataScience/signal-hygiene/signal-name-normalization");
const MULTIPLICATION_SIGNS = new Set(['×', '·', '⋅', '*']);
const MINUS_SIGNS = new Set(['-', '−']);
const OPERATORS = new Set(['+', '-', '*', '/', '(', ')']);
var TokenKind;
(function (TokenKind) {
    TokenKind["Operand"] = "operand";
    TokenKind["Operator"] = "operator";
    TokenKind["OpenParen"] = "open_paren";
    TokenKind["CloseParen"] = "close_paren";
})(TokenKind || (TokenKind = {}));
function isInfixPosition(previous, hasFollowing) {
    if (previous === undefined || !hasFollowing) {
        return false;
    }
    return previous.kind === TokenKind.Operand || previous.kind === TokenKind.CloseParen;
}
function unitSpellings(declaration) {
    const spellings = [declaration.short, declaration.long, ...(declaration.alternateNames ?? [])];
    return spellings
        .filter((spelling) => spelling !== undefined && spelling !== '')
        .sort((a, b) => b.length - a.length);
}
function stripTrailingUnit(trimmed, declaration) {
    const lowered = trimmed.toLowerCase();
    for (const spelling of unitSpellings(declaration)) {
        const suffix = spelling.toLowerCase();
        if (!lowered.endsWith(suffix) || lowered.length === suffix.length) {
            continue;
        }
        const head = trimmed.slice(0, trimmed.length - suffix.length);
        if (/\d\s*$/.test(head)) {
            return head;
        }
    }
    return trimmed;
}
const UNARY_SIGNS = new Set(['+', '-']);
function isWellFormed(tokens) {
    let depth = 0;
    let operandDue = true;
    for (const token of tokens) {
        switch (token.kind) {
            case TokenKind.Operator:
                if (operandDue && !UNARY_SIGNS.has(token.text)) {
                    return false;
                }
                operandDue = true;
                break;
            case TokenKind.OpenParen:
                if (!operandDue) {
                    return false;
                }
                depth += 1;
                break;
            case TokenKind.CloseParen:
                if (operandDue || depth === 0) {
                    return false;
                }
                depth -= 1;
                break;
            case TokenKind.Operand:
                if (!operandDue && token.spaced) {
                    return false;
                }
                operandDue = false;
                break;
        }
    }
    return !operandDue && depth === 0;
}
function tokenize(input) {
    const tokens = [];
    let index = 0;
    let spaced = false;
    function push(token) {
        tokens.push({ ...token, spaced });
        spaced = false;
    }
    while (index < input.length) {
        const character = input[index] ?? '';
        if (/\s/.test(character)) {
            spaced = true;
            index += 1;
            continue;
        }
        if (MULTIPLICATION_SIGNS.has(character)) {
            push({ kind: TokenKind.Operator, text: '*' });
            index += 1;
            continue;
        }
        if (MINUS_SIGNS.has(character)) {
            push({ kind: TokenKind.Operator, text: '-' });
            index += 1;
            continue;
        }
        if (OPERATORS.has(character)) {
            const kind = character === '(' ? TokenKind.OpenParen
                : character === ')' ? TokenKind.CloseParen
                    : TokenKind.Operator;
            push({ kind, text: character });
            index += 1;
            continue;
        }
        const number = /^\d*\.?\d+/.exec(input.slice(index));
        if (number !== null) {
            const magnitude = Number(number[0]);
            if (!Number.isFinite(magnitude)) {
                return null;
            }
            push({ kind: TokenKind.Operand, text: String(magnitude) });
            index += number[0].length;
            continue;
        }
        const identifier = /^[A-Za-z_][A-Za-z0-9_.]*/.exec(input.slice(index));
        if (identifier === null) {
            return null;
        }
        const text = identifier[0];
        const rest = input.slice(index + text.length).trim();
        if ((text === 'x' || text === 'X') && isInfixPosition(tokens[tokens.length - 1], rest !== '')) {
            push({ kind: TokenKind.Operator, text: '*' });
        }
        else {
            push({ kind: TokenKind.Operand, text: (0, signal_name_normalization_1.normalizeSignalNameForComparison)(text) });
        }
        index += text.length;
    }
    return tokens.length > 0 && isWellFormed(tokens) ? tokens : null;
}
function canonicalizeProductOrder(tokens) {
    const [left, operator, right] = tokens;
    if (tokens.length !== 3 ||
        left === undefined ||
        right === undefined ||
        operator?.text !== '*' ||
        left.kind !== TokenKind.Operand ||
        right.kind !== TokenKind.Operand) {
        return tokens;
    }
    return left.text <= right.text ? tokens : [right, operator, left];
}
function foldSymbolicValue(raw, declaration) {
    const trimmed = raw?.trim() ?? '';
    if (trimmed === '') {
        return '';
    }
    const tokens = tokenize(stripTrailingUnit(trimmed, declaration));
    if (tokens === null) {
        return trimmed;
    }
    return canonicalizeProductOrder(tokens)
        .map((token) => token.text)
        .join('');
}
//# sourceMappingURL=symbolic-comparison.js.map